import React, { useEffect, useState, useRef, useCallback } from "react";
import { gsap } from "gsap";
import "../../styles/pageTransition.scss";

/**
 * PageTransition component that provides a fade transition with noise effect
 * between page navigations in Gatsby
 */
const PageTransition = ({ children, location }) => {
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitioning, setTransitioning] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [transitionPhase, setTransitionPhase] = useState("idle"); // idle, entering, entered, exiting
  const containerRef = useRef(null);
  const noiseRef = useRef(null);
  const prevChildrenRef = useRef(children);
  const transitionTimeoutRef = useRef(null);

  // Generate noise pattern canvas
  useEffect(() => {
    if (typeof window === "undefined" || !noiseRef.current) return;
    
    const canvas = noiseRef.current;
    const ctx = canvas.getContext("2d");
    
    // Set canvas dimensions to match window
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      generateNoise();
    };
    
    // Generate the noise pattern
    const generateNoise = () => {
      const imageData = ctx.createImageData(canvas.width, canvas.height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        // Random noise value (0-255)
        const noise = Math.floor(Math.random() * 255);
        
        // Set RGB values (all the same for grayscale)
        data[i] = noise;     // R
        data[i + 1] = noise; // G
        data[i + 2] = noise; // B
        data[i + 3] = 40;    // Alpha (40 for subtle effect)
      }
      
      ctx.putImageData(imageData, 0, 0);
    };
    
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    
    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  // Generate noise pattern function
  const generateNoise = useCallback(() => {
    if (!noiseRef.current) return;
    
    const ctx = noiseRef.current.getContext("2d");
    const imageData = ctx.getImageData(0, 0, noiseRef.current.width, noiseRef.current.height);
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const noise = Math.floor(Math.random() * 255);
      data[i] = noise;
      data[i + 1] = noise;
      data[i + 2] = noise;
    }
    
    ctx.putImageData(imageData, 0, 0);
  }, []);

  // Handle initial render - don't show transition on first load
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
    }
    
    // Cleanup function to clear any pending timeouts
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [isInitialRender]);

  // Handle page transitions with proper loading sequence
  useEffect(() => {
    // Skip transition on initial render
    if (isInitialRender) return;
    
    // Skip if already transitioning or if children haven't changed
    if (children === prevChildrenRef.current) return;
    
    // Clear any existing timeouts
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    
    // Start transition sequence
    const startTransition = () => {
      // Set transitioning state
      setTransitioning(true);
      setTransitionPhase("entering");
      
      // Fade in the overlay with noise
      gsap.to(".page-transition-overlay", {
        duration: 0.4,
        autoAlpha: 1,
        ease: "power2.inOut",
        onComplete: () => {
          // Update the children reference
          prevChildrenRef.current = children;
          
          // Set the new children
          setDisplayChildren(children);
          setTransitionPhase("entered");
          
          // Wait for images and resources to load
          transitionTimeoutRef.current = setTimeout(() => {
            // Generate new noise pattern
            generateNoise();
            
            // Wait a bit longer to ensure everything is rendered
            transitionTimeoutRef.current = setTimeout(() => {
              // Complete transition by fading out overlay
              gsap.to(".page-transition-overlay", {
                duration: 0.4,
                autoAlpha: 0,
                ease: "power2.inOut",
                onComplete: () => {
                  setTransitioning(false);
                  setTransitionPhase("idle");
                }
              });
            }, 300); // Additional delay to ensure page is fully rendered
          }, 200); // Wait for content to load
        }
      });
    };
    
    // Start the transition
    startTransition();
    
  }, [children, isInitialRender, generateNoise]);

  return (
    <div className="page-transition-container" ref={containerRef}>
      {/* The actual page content */}
      <div className={`page-content ${transitioning ? 'transitioning' : ''} ${transitionPhase}`}>
        {displayChildren}
      </div>
      
      {/* Overlay with noise effect for transitions */}
      <div className="page-transition-overlay">
        <canvas ref={noiseRef} className="noise-canvas"></canvas>
      </div>
    </div>
  );
};

export default PageTransition;
